import { useState, useEffect, useContext, useReducer, useMemo } from "react";
import { useNavigate } from 'react-router-dom';
import { useTable, usePagination, useFilters, useSortBy } from 'react-table';
import "../../css/ListUsers.css";
import Header from "../Navigation/Header";
import Footer from "../Navigation/Footer";
//import CompanyReadOnlyRow from "../../components/CompanyReadOnlyRow";
import CompanyAppAccessTable from "../../components/CompanyAppAccessTable";
import CompanyModal from "../../components/CompanyModal";
import FetchUtil from "../../utils/FetchUtil";
import UserContext from "../../contexts/UserContext";
import { useQuery } from 'react-query';
import CompanyFilter from "./DataFilter";
import { useTranslation } from 'react-i18next';
import { scrollToTop } from "../../utils/helper.js";
import { matchSorterFn, sortItems } from "../../utils/sorting";
import { TextSearchFilter } from "../../utils/filter";

const initialState = {
	queryPageIndex: 0,
	queryPageSize: 25,
	totalCount: 0,
	queryPageFilter: "",
};

const PAGE_CHANGED = 'PAGE_CHANGED';
const TOTAL_COUNT_CHANGED = 'TOTAL_COUNT_CHANGED';
const PAGE_FILTER_CHANGED = 'PAGE_FILTER_CHANGED';

const reducer = (state, { type, payload, t }) => {
	switch (type) {
		case PAGE_CHANGED:
			return {
				...state,
				queryPageIndex: payload,
			};
		case TOTAL_COUNT_CHANGED:
			return {
				...state,
				totalCount: payload,
			};
		case PAGE_FILTER_CHANGED:
			return {
				...state,
				queryPageFilter: payload.trim(),
				queryPageIndex: 0,
			};
		default:
			throw new Error(t("error.unhandledException") + " : ${type}");
	}
};

let prevCount = 0;
function setPrevCount(newCount) {
	prevCount = newCount;
}

function ListCompanies() {
	const navigate = useNavigate();
	const [show, setShow] = useState(false);
	const [add, setAdd] = useState(false);
	const [errorValue, setErrorValue] = useState("");
	const [data, setData] = useState([]);
	const [keyCount, setKeyCount] = useState(0);
	const [count, setCount] = useState(0);
	const [keyword, setKeyword] = useState("");
	const [useFilter, setUseFilter] = useState(false);
	const [appAccess, setAppAccess] = useState(false);
	const [appNames, setAppNames] = useState([]);
	const { t } = useTranslation();
	const userCtx = useContext(UserContext);

	//const [columns, setColumns] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const defaultColumn = useMemo(() => ({
						      // Let's set up our default Filter UI
						      Filter: ""
						    }),[]
  						);
  	const filterTypes = useMemo(() => ({
					      rankedMatchSorter: matchSorterFn
					    }),
					    []
					  );
	const columns = useMemo(
		() => [
			{
				id: 'companyName',
				accessor: 'companyName',
				Header: () => t("page.name"),
				minWidth: '15vw',
				maxWidth: '15vw',
				Filter: TextSearchFilter,
    			filter: "rankedMatchSorter",
    			sortType: (prev, curr, columnId) => {
					return sortItems(prev, curr, columnId);
				},
			},
			{
				id: 'companyTypeDesc',
				accessor: 'companyTypeDesc',
				Header: () => t("page.company.type"),
				minWidth: '10vw',
				maxWidth: '10vw',
				Filter: TextSearchFilter,
    			filter: "rankedMatchSorter",
    			sortType: (prev, curr, columnId) => {
					return sortItems(prev, curr, columnId);
				},
			},
			{
				accessor: 'address',
				Header: () => t("page.address"),
				minWidth: '15vw',
				maxWidth: '20vw',
				disableSortBy: true,
			},
			{
				accessor: 'phone',
				Header: () => t("page.phone"),
				minWidth: '5vw',
				maxWidth: '5vw',
				disableSortBy: true,
			},
			{
				accessor: 'appNames',
				Header: () => t("page.appAccess"),
				minWidth: '15vw',
				maxWidth: '20vw',
				disableSortBy: true,
			},
			{
				id: 'status',
				accessor: 'status',
				Header: () => t("page.status"),
				minWidth: '5vw',
				maxWidth: '5vw',
				disableSortBy: true,
				sortType: (prev, curr, columnId) => {
					return sortItems(prev, curr, columnId);
				},
			},
			{
				accessor: 'action',
				id: 'action',
				Header: () => t("page.action"),
				minWidth: '10vw',
				maxWidth: '20vw',
				disableSortBy: true,
			},
			{
			    Header: 'companyId',
			    accessor: 'companyId',
			    disableSortBy: true,
			    disableFilters: true,
			},
		],
		[]
	)

	const pageData = useMemo(()=>{
	    return data ? data : []
	}, [data]);

	useEffect(() => {
		fetchAppNames();
	}, []);

	const initialValues = {
		companyId: 0,
		companyName: "",
		companyTypeKey: 0,
		companyGroup: "",
		address1: "",
		address2: "",
		phone: "",
		zipCode: "",
		zipExt: "",
		city: "",
		stateKey: 0,
		stateCode: "",
		stateName: "",
		companyTimeZone: "America/Los_Angeles",
		countryCode: "US",
		countryName: "United States of America",
		companyTypeAliasKey: 0,
		realCompanyName: "",
		contactName: "",
		contactEmail: "",
		companyURL: "",
		companyLanguage: "en",
		geocode: "",
		latitude: "",
		longitude: "",
		unitOfMeasure: "lb",
		multiCompany: false,
		appAccess: [],
	};
	const [editFormData, setEditFormData] = useState(initialValues);

	const [{ queryPageIndex, queryPageSize, totalCount, queryPageFilter }, dispatch] =
		useReducer(reducer, initialState);

	const { isError, error, isSuccess, refetch } = useQuery(
		['company-list', queryPageIndex, queryPageSize, queryPageFilter],
		() => retrieveCompanies(queryPageIndex, queryPageSize, queryPageFilter),
		{
			keepPreviousData: false,
			staleTime: 1000,
			refetchOnWindowFocus: false,
			refetchOnReconnect: false,
		}
	);


	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		canPreviousPage,
		canNextPage,
		pageOptions,
		pageCount,
		gotoPage,
		nextPage,
		previousPage,
		setPageSize,
		rows,
		prepareRow,
		// Get the state from the instance
		state: { pageIndex, pageSize },
	} = useTable(
		{
			columns: columns,
			data: pageData,
			filterTypes,
			defaultColumn,
			initialState: {
				pageIndex: queryPageIndex,
				pageSize: queryPageSize,
				sortBy: [
	                {
	                    id: 'status',
	                    desc: false
	                },
	                {
	                    id: 'companyName',
	                    desc: false
	                },
	                {
	                    id: 'companyTypeDesc',
	                    desc: false
	                }
                ]
			},
			manualPagination: true,
			pageCount: isSuccess ? Math.ceil(count / queryPageSize) : null,
		},
		useSortBy, usePagination, useFilters
	);

	useEffect(() => {
		dispatch({ type: PAGE_CHANGED, payload: pageIndex, t: t });
	}, [pageIndex]);

	useEffect(() => {
		if (count !== prevCount) {
			dispatch({
				type: TOTAL_COUNT_CHANGED,
				payload: count,
				t: t
			});

			setPrevCount(count);
		}
	}, [count]);

	useEffect(() => {
		if (useFilter) {
			dispatch({ type: PAGE_FILTER_CHANGED, payload: keyword, t: t });
			gotoPage(0);
		}

	}, [keyword, useFilter, gotoPage]);


	function fetchAppNames() {
		const requestOptions = {
			credentials: "same-origin",
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ source: "web", getAll: false }),
		};
		try {
			FetchUtil.fetchAPI("MGetAppNames", "/Service/StaticHelper/GetAppNames", requestOptions, getAppNamesSuccess, getAppNamesFail);
		} catch (error) {
			setErrorValue(error.message);
		}
	}

	function getAppNamesFail(redirect) {
		setErrorValue(t("error.getAppNamesFailed"));
	}
	function getAppNamesSuccess(responseData) {
		let appNameResponse;
		if (responseData) {
			appNameResponse = responseData.getAppNameResponse;
			if (appNameResponse && appNameResponse.errorCode === "0") {
				setAppNames([...appNames, ...appNameResponse.appNames]);
			} else if (appNameResponse) {
				setErrorValue(t("error.error") + " " + appNameResponse.errorMessage);
			}
		}
	}

	function retrieveCompanies(page, dispSize, searchFilter) {
		setErrorValue("");
		setIsLoading(true);
		const offset = page * dispSize;
		const requestOptions = {
			credentials: "same-origin",
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				source: "web", getAll: true,
				offset: offset, limit: dispSize, searchValue: searchFilter
			}),
		};
		try {
			FetchUtil.fetchAPI("FetchCompanies", "/Service/CompanyService/FetchCompanies", requestOptions, retrieveCompaniesSuccess, retrieveCompaniesFail);
		} catch (error) {
			setIsLoading(false);
			throw new Error(error.message);
		}
	}

	function retrieveCompaniesFail(redirect) {
		setIsLoading(false);
		if (redirect) {
			navigate("/");
		}
		setErrorValue(t("error.companyListNotRetrieved"));
	}

	function retrieveCompaniesSuccess(responseData) {
		let fetchCompaniesResponse;
		if (responseData) {
			fetchCompaniesResponse = responseData.fetchCompaniesResponse;
			if (fetchCompaniesResponse && fetchCompaniesResponse.errorCode === "0") {
				setData(fetchCompaniesResponse.companies.slice());
				if (fetchCompaniesResponse.offset === 0) {
					setCount(fetchCompaniesResponse.totalCount);
				}
			} else if (fetchCompaniesResponse && fetchCompaniesResponse.errorCode === "114") {
				navigate("/", {
					state: {
						errorMessage: t("error.sessionTimeout"),
					},
				});
			} else if (fetchCompaniesResponse) {
				setErrorValue(t("error.error") + " " + fetchCompaniesResponse.errorMessage);
			} else {
				setErrorValue(t("error.system.fetch"));
			}
		}
		setIsLoading(false);
	}

	const handleEditClick = (event, company) => {
		event.preventDefault();
		event.stopPropagation();
		setErrorValue("");
		let formData = JSON.parse(JSON.stringify(company));
		setEditFormData({ ...editFormData, ...formData });
		setShow(true);
		setAdd(false);
		setAppAccess(false);
	};
	const handleDeleteClick = (event, companyId, statusCode) => {
		event.preventDefault();
		setErrorValue("");
		deleteCompany(companyId, statusCode);
	};

	const handleAddClick = (event) => {
		event.preventDefault();
		setErrorValue("");
		setShow(true);
		setAdd(true);
		setAppAccess(false);
		setEditFormData(initialValues);
	}
	const deleteCompany = (companyId, statusCode) => {
		const requestOptions = {
			credentials: "same-origin",
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				source: "web", companyId: companyId,
				statusCode: statusCode, updatedById: userCtx.loggedInUserId
			}),
		};
		try {
			FetchUtil.fetchAPI("DeleteCompany", "/Service/CompanyService/DeleteCompany", requestOptions, deleteCompanySuccess, deleteCompanyFail);
		} catch (error) {
			setErrorValue(error.message);
		}
	}

	function deleteCompanyFail(redirect) {
		if (redirect) {
			navigate("/");
		}
		setErrorValue(t("error.companyDeleteFailed"));
	}
	function deleteCompanySuccess(responseData) {
		let deleteCompanyResponse;
		if (responseData) {
			deleteCompanyResponse = responseData.deleteCompanyResponse;
			if (deleteCompanyResponse && deleteCompanyResponse.errorCode === "0") {
				// only retrieve the modified user
				handleRefresh();
			} else if (deleteCompanyResponse && deleteCompanyResponse.errorCode === "102") {
				handleRefresh();
				setErrorValue(t("error.error") + " " + deleteCompanyResponse.errorMessage);

			} else if (deleteCompanyResponse) {
				setErrorValue(t("error.error") + " " + deleteCompanyResponse.errorMessage);
			} else {
				setErrorValue(t("error.system.update"));
			}
		}
	}

	const onClickFilterCallback = (searchFilter) => {
		if (searchFilter === keyword) {
			return
		}
		setUseFilter(true);
		setKeyword(searchFilter);
	}
	if (isError) {
		setErrorValue(t("error.error") + " " + error.message);
	}
	const handleAppAccessClick = (event, company) => {
		event.preventDefault();
		event.stopPropagation();
		setErrorValue("");
		let formData = JSON.parse(JSON.stringify(company));
		setEditFormData({ ...editFormData, ...formData });
		setShow(false);
		setAdd(false);
		scrollToTop();
		setAppAccess(true);
	};
	const handleRefresh = () => {
		refetch();
		setKeyCount(prevCount => prevCount + 1);
	};
	return (
		<div className="container-fluid">
			<Header />
			<div className={(show || appAccess) ? "container-fluid container_list viewh-85 pe-none" : "container-fluid container_list viewh-85"}>
				{userCtx.role === "SiteAdmin" ?
					<><div className="row">
						<div className="col">
							<div
								id="inerrorMessage"
								className={
									(errorValue) ? "errorblock" : "errorblock hide"
								}
							>
								{errorValue ? errorValue : ""}
							</div>
						</div>
					</div>

						<div className="row">
							<div className="col">
								<div id="placeholder_1" className="placeholder-pg"></div>
							</div>
						</div>
						<div className="row">
							<div className="col">
								<CompanyFilter t={t} onClickFilterCallback={onClickFilterCallback} defaultKeyword={keyword} />
							</div>
							{(isLoading) && (<div> <span class="spinner-border spinner-border-sm spinner-lg-size"></span> </div>)}
						</div>
						<div className="row">
							<div className="col">
								<div id="placeholder_2" className="placeholder-pg"></div>
							</div>
						</div>
						<div className="row">
							<div className="col">
								<button className="btn btn-primary btn-block me-4" data-toggle="modal" data-target="#companyModal"
									onClick={(event) => handleAddClick(event)}>{t("page.company.add")}</button>
							</div>
						</div>
						<div className="row">
							<div className="col">
								{isSuccess ? (
									<>
										<table key={keyCount} className="list-table" {...getTableProps()}>
											<thead>
												{headerGroups.map(headerGroup => (
													<tr {...headerGroup.getHeaderGroupProps()}>
														{headerGroup.headers.map(column => (
															(column.Header !== "companyId") && 
															<th className="list-th" {...column.getHeaderProps(column.getSortByToggleProps(), {
																style: { maxWidth: column.maxWidth },
															})}>{column.render("Header")}
															<span className={
																(count >0 && column.canSort)? column.isSorted
																	? column.isSortedDesc
																		? "fa fa-sort-down"
																		: "fa fa-sort-up"
																	: "fa fa-sort"
																	: ""
																} style={{  fontSize:'24px', color:'#0c374c', float: 'right', verticalAlign: 'middle' }}>
															</span>
															<div>{column.canFilter ? column.render("Filter") : null}</div>
															</th>
														))}
													</tr>
												))}
											</thead>
											<tbody {...getTableBodyProps()}>
												{rows.map((row, index) => {
													prepareRow(row);
													let oddIdx = ((index % 2) > 0) ? true : false;
													let company;
													let found = false;
													pageData.map((eachCompany) => {
														if(eachCompany.companyId == row.values.companyId){
															company = eachCompany;
															found = true;
														}
														if(found){
															return;
														}
													});
													return (
														<tr	{...row.getRowProps()}>
															{row.cells.map((cell) => {
																return (
																	(cell.column.id == "action")? 
																	<td className={oddIdx?"list-td col col-md-auto odd": "list-td col col-md-auto even"}> 
															            <button className="btn btn-primary btn-block me-2 mb-1" data-toggle="modal" data-target="#companyModal"
															             	onClick={(event) => handleEditClick(event, company)}>{t("page.edit")}</button>
															            <button className="btn btn-primary btn-block me-2 mb-1" onClick={(event) => handleDeleteClick(event, company.companyId, company.statusCode)}>{company.statusCode === 1? t("page.delete") : t("page.undelete")}</button>
															            <button className="btn btn-primary btn-block me-2 mb-1" onClick={(event) => handleAppAccessClick(event, company)}>{ t("page.appAccess") }</button>
														             </td>
																	:
																	(cell.column.id !== "companyId") && 
																	<td
																		{...cell.getCellProps()}
																		className={oddIdx?"list-td col col-md-auto text-wrap odd": "list-td col col-md-auto text-wrap even"}
																		style={{
																			minWidth: cell.column.minWidth,
																			maxWidth: cell.column.maxWidth,
																			wordBreak: "break-word",
																		}}
																	>
																		{cell.render("Cell")}
																	</td>
																);
															})}
														</tr>
													);
		
												})}
											</tbody>

										</table>

										<div className="pagination">
											<button className="nav-button" onClick={() => { gotoPage(0); }} disabled={!canPreviousPage}>
												{'<<'}
											</button>
											<button className="nav-button" onClick={() => { previousPage(); }} disabled={!canPreviousPage}>
												{'<'}
											</button>
											<button className="nav-button" onClick={() => { nextPage(); }} disabled={!canNextPage}>
												{'>'}
											</button>
											<button className="nav-button"
												onClick={() => { gotoPage(pageCount - 1); }}
												disabled={!canNextPage}
											>
												{'>>'}
											</button>
											<span className="nav-page-info">{t("page.page") + ' '}
												<strong>
													{pageOptions.length > 0 ? pageIndex + 1 : 0} {t("page.of")} {pageOptions.length}
												</strong>
											</span>
											<span className="nav-page-goto">{t("page.goToPage")}:{' '}
												<input
													type="number"
													value={pageIndex + 1}
													onChange={(e) => {
														const page = e.target.value ? Number(e.target.value) - 1 : 0;
														gotoPage(page);
													}}
													className="nav-goto-input"
												/>
											</span>
										</div>
									</>
								) : null}
							</div>
						</div>
						<div className="row">
							<div className="col">
								<button className="btn btn-primary btn-block me-4" data-toggle="modal" data-target="#companyModal"
									onClick={(event) => handleAddClick(event)}>{t("page.company.add")}</button>
							</div>
						</div>
						<div className="row">
							<div className="col">
								<div
									id="berrorMessage"
									className={
										(errorValue) ? "errorblock" : "errorblock hide"
									}
								>
									{errorValue ? errorValue : ""}
								</div>
							</div>
						</div>
					</>
					: null}
			</div>
			<div className="footer_container">
				<Footer />
			</div>
			{show && <CompanyModal key={keyCount} t={t} company={editFormData} setShow={setShow} refetch={handleRefresh} add={add} />}
			{appAccess &&
				<CompanyAppAccessTable key={keyCount} setAppAccess={setAppAccess} appAccess={editFormData.appAccess}
					appNames={appNames} companyId={editFormData.companyId} refetch={handleRefresh} />}
		</div>
	);
}

export default ListCompanies;